import { useState, useEffect } from "react";
import api from "../api/api"; // adjust the relative path if dashboard.jsx isn't a sibling of api.js
import {
  Users,
  UserPlus,
  Briefcase,
  IndianRupee,
  Target,
  ArrowUpRight,
  ArrowLeft,
  BarChart3
} from 'lucide-react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom'

// ---- Indian currency helpers -------------------------------------------
// Full grouped rupee value, e.g. 642890 -> "₹6,42,890"
const formatINR = (value) =>
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value)}`;

// Compact Lakh / Crore value, e.g. 1850000 -> "₹18.5L", 125000000 -> "₹12.5Cr"
const formatCompactINR = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1e7) return `₹${(value / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(value / 1e5).toFixed(1)}L`;
  if (abs >= 1e3) return `₹${(value / 1e3).toFixed(1)}K`;
  return formatINR(value);
};

// Round a value up to a clean axis ceiling (1 / 2 / 5 / 10 × a power of ten) so chart
// gridlines and scaling stay proportionate no matter what the underlying data is.
const getNiceMax = (value) => {
  if (!value || value <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
};

// Benchmark reference line for the quarterly trend chart. Not sourced from the
// database (only "sales" — the PO value — is live); kept as a static comparison
// line alongside the live figures. Adjust/remove if you don't need a benchmark.
const QUARTER_BENCHMARKS = { Q1: 100000, Q2: 140000, Q3: 155000, Q4: 170000 };

export default function Dashboard({ dashboardType = "sales" }) {
  const [activeQuarterTab, setActiveQuarterTab] = useState("All");

  // ---- Live dashboard data (items i-v) -----------------------------------
  const [metrics, setMetrics] = useState(null); // { totalCustomers, totalOpportunities, totalClosures, closuresValue, totalSales, quarterlySales, fyLabel }
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(null);
  const navigate = useNavigate()

  // When an admin opens a user from the Users list, the route carries that
  // user's id (/dashboard/:userId) so this page loads THEIR dashboard
  // instead of the logged-in admin's own. Visiting plain /dashboard (no
  // param) keeps the original "my own dashboard" behavior.
  const { userId } = useParams();
  const location = useLocation();
  const viewedUserName = location.state?.userName;
  const isPresalesDashboard = dashboardType === "presales";
  const isBidManagerDashboard = dashboardType === "bidmanager";
  const dashboardTitle = isPresalesDashboard
    ? "Presales Dashboard"
    : isBidManagerDashboard
    ? "Bid Manager Dashboard"
    : "Sales Dashboard";
  const dashboardDescription = isPresalesDashboard
    ? "Presales pipeline & opportunity performance"
    : isBidManagerDashboard
    ? "Bid pipeline & bid performance"
    : "Sales pipeline & sales performance";

  useEffect(() => {
    let cancelled = false;

    async function loadMetrics() {
      try {
        setMetricsLoading(true);
        setMetricsError(null);
        // Uses the shared axios instance from ./api so the token is attached
        // and expired-token refresh is handled the same way as your other
        // endpoints (leads, deals, etc). When userId is present (admin
        // viewing a specific user), it's passed through so the backend can
        // scope the metrics to that user instead of the logged-in caller.
        const res = await api.get("/dashboard/metrics", {
          params: userId ? { userId } : undefined,
        });
        if (!cancelled) setMetrics(res.data);
      } catch (err) {
        if (!cancelled) {
          setMetricsError(
            err.response?.data?.error || err.message || "Failed to load dashboard data"
          );
        }
      } finally {
        if (!cancelled) setMetricsLoading(false);
      }
    }

    loadMetrics();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const isAdmin = !!metrics?.isAdmin;
  const isViewingOtherUser = !!userId;

  // Quarter-filtered figures for all four top cards. "All" shows the
  // lifetime totals (unscoped to the current FY, same as before); picking a
  // specific quarter narrows every card to that FY quarter's numbers.
  // Closures/Sales are scoped by opf_orders.created_at, Opportunities by
  // leads.ld_last_updated, and Customers by customers.cust_last_updated.
  const selectedQuarterClosures =
    activeQuarterTab !== "All"
      ? (metrics?.quarterlyClosures || []).find((q) => q.quarter === activeQuarterTab)
      : null;
  const selectedQuarterSales =
    activeQuarterTab !== "All"
      ? (metrics?.quarterlySales || []).find((q) => q.quarter === activeQuarterTab)
      : null;
  const selectedQuarterCustomers =
    activeQuarterTab !== "All"
      ? (metrics?.quarterlyCustomers || []).find((q) => q.quarter === activeQuarterTab)
      : null;
  const selectedQuarterOpportunities =
    activeQuarterTab !== "All"
      ? (metrics?.quarterlyOpportunities || []).find((q) => q.quarter === activeQuarterTab)
      : null;

  const displayedClosuresCount = selectedQuarterClosures
    ? selectedQuarterClosures.count
    : metrics?.totalClosures ?? 0;
  const displayedClosuresValue = selectedQuarterClosures
    ? selectedQuarterClosures.value
    : metrics?.closuresValue ?? 0;
  const displayedSales = selectedQuarterSales ? selectedQuarterSales.sales : metrics?.totalSales ?? 0;
  const displayedCustomersCount = selectedQuarterCustomers
    ? selectedQuarterCustomers.count
    : metrics?.totalCustomers ?? 0;
  const displayedOpportunitiesCount = selectedQuarterOpportunities
    ? selectedQuarterOpportunities.count
    : metrics?.totalOpportunities ?? 0;

  // Core Metrics (Items i to iv) — built from live `metrics` once loaded
  const topMetrics = [
    {
      label: "Total Customers",
      path: "/customers",
      value: metricsLoading ? "—" : displayedCustomersCount.toLocaleString("en-IN"),
      icon: Users,
      color: "blue",
      description: "Active business accounts",
      subtext:
        (isViewingOtherUser
          ? `Accounts assigned to ${viewedUserName || "this user"}`
          : isAdmin
          ? "All accounts across the team"
          : "Accounts assigned to you") +
        (activeQuarterTab === "All" ? "" : ` · ${activeQuarterTab}`)
    },
    {
      label: "Total opportunities",
      path: "/leads",
      value: metricsLoading ? "—" : displayedOpportunitiesCount.toLocaleString("en-IN"),
      icon: UserPlus,
      color: "blue",
      description: "Pipeline prospects logged",
      subtext:
        (isViewingOtherUser
          ? `Leads assigned to ${viewedUserName || "this user"}`
          : isAdmin
          ? "All leads across the team"
          : "Leads assigned to you") +
        (activeQuarterTab === "All" ? "" : ` · ${activeQuarterTab}`)
    },
    {
      label: "Total closures",
      path: "/deals",
      value: metricsLoading ? "—" : displayedClosuresCount.toLocaleString("en-IN"),
      icon: Briefcase,
      color: "amber",
      description: "OPFs with status: submitted",
      subtext: metricsLoading
        ? ""
        : `Valued at ${formatCompactINR(displayedClosuresValue)}${
            activeQuarterTab === "All" ? " overall" : ` in ${activeQuarterTab}`
          }`
    },
    {
      label: "Total Sales",
      value: metricsLoading ? "—" : formatINR(displayedSales),
      icon: IndianRupee,
      color: "green",
      description: "Gross PO value across all OPFs",
      subtext: metricsLoading
        ? ""
        : activeQuarterTab === "All"
        ? (metrics?.fyLabel ? `As of ${metrics.fyLabel}` : "")
        : `${activeQuarterTab} · ${metrics?.fyLabel || ""}`
    }
  ];

  // Item v: Quarterly Sales Trend line chart — live PO-value totals per quarter,
  // benchmark stays a static reference line (see QUARTER_BENCHMARKS above).
  const allSalesTrendData = (metrics?.quarterlySales ?? ["Q1", "Q2", "Q3", "Q4"].map((q) => ({ quarter: q, sales: 0 }))).map(
    (d) => ({ ...d, benchmark: QUARTER_BENCHMARKS[d.quarter] ?? 0 })
  );
  // The quarter filter narrows the chart to a single quarter; "All" shows the full year.
  const salesTrendData =
    activeQuarterTab === "All" ? allSalesTrendData : allSalesTrendData.filter((d) => d.quarter === activeQuarterTab);
  // Fixed internal coordinate space for the SVG (independent of rendered size) so the
  // chart geometry is computed once from the data rather than hand-placed per point.
  const trendSvgWidth = 640;
  const trendSvgHeight = 176;
  const trendPadX = 40;
  const trendTopMargin = 12;
  // Axis scale stays anchored to the full FY range (not just the filtered slice) so a
  // single quarter can still be read in context instead of the chart rescaling around it.
  const trendMaxRaw = Math.max(...allSalesTrendData.map((d) => Math.max(d.sales, d.benchmark)), 0);
  const trendMax = getNiceMax(trendMaxRaw);
  const trendGridlines = [1, 0.75, 0.5, 0.25].map((f) => trendMax * f);
  const trendPlotWidth = trendSvgWidth - trendPadX * 2;
  const trendXStep = salesTrendData.length > 1 ? trendPlotWidth / (salesTrendData.length - 1) : 0;
  const trendYForValue = (value) =>
    trendSvgHeight - trendTopMargin - (value / trendMax) * (trendSvgHeight - trendTopMargin);
  const trendPoints = salesTrendData.map((d, i) => ({
    // A single filtered quarter is centered rather than pinned to the left edge.
    x: salesTrendData.length === 1 ? trendPadX + trendPlotWidth / 2 : trendPadX + i * trendXStep,
    ySales: trendYForValue(d.sales),
    yBenchmark: trendYForValue(d.benchmark)
  }));
  const salesLinePath =
    trendPoints.length > 1 ? trendPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.ySales}`).join(" ") : "";
  const benchmarkLinePath =
    trendPoints.length > 1 ? trendPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.yBenchmark}`).join(" ") : "";
  const salesAreaPath =
    trendPoints.length > 1
      ? `M ${trendPoints[0].x} ${trendSvgHeight} ` +
        trendPoints.map((p) => `L ${p.x} ${p.ySales}`).join(" ") +
        ` L ${trendPoints[trendPoints.length - 1].x} ${trendSvgHeight} Z`
      : "";
  const peakQuarter = salesTrendData.reduce((max, d) => (d.sales > max.sales ? d : max), salesTrendData[0]);

  // ---- Bottom/top line targets + FY-ordered month/quarter data from the API --
  // Each period has a bottom line and a top line. (An older API that still
  // sends a single number is treated as top line with bottom line 0.)
  const toLine = (v) =>
    typeof v === "number"
      ? { bottom: 0, top: v }
      : { bottom: Number(v?.bottom) || 0, top: Number(v?.top) || 0 };
  const targets = {
    monthly: toLine(metrics?.targets?.monthly),
    quarterly: toLine(metrics?.targets?.quarterly),
    yearly: toLine(metrics?.targets?.yearly),
  };

  // Achievement % = (achieved - bottom line) / (top line - bottom line) * 100.
  // 0% = on the bottom line, 100% = on the top line. Below the bottom line
  // counts as 0% (no negative bars); above the top line goes past 100%.
  const calcAchievementPct = (achieved, bottom, top) =>
    top > bottom ? Math.max(0, ((achieved - bottom) / (top - bottom)) * 100) : 0;
  const currentMonthLabel = metrics?.currentMonthLabel;
  const currentQuarterLabel = metrics?.currentQuarterLabel;

  const monthlyReached =
    (metrics?.monthlySales || []).find((m) => m.month === currentMonthLabel)?.sales ?? 0;
  const quarterlyReached =
    (metrics?.quarterlySales || []).find((q) => q.quarter === currentQuarterLabel)?.sales ?? 0;
  const yearlyReached =
    metrics?.fyTotalSales ?? (metrics?.quarterlySales || []).reduce((s, q) => s + q.sales, 0);

  // Maps an achievement % to a status label + bar color, reused by both the
  // Target Assignments panel and (for consistency) could be reused elsewhere.
  const statusFromProgress = (pct) => {
    if (pct >= 100) return { status: "Achieved", color: "bg-emerald-500" };
    if (pct >= 75) return { status: "On Track", color: "bg-blue-500" };
    if (pct >= 40) return { status: "In Progress", color: "bg-amber-500" };
    return { status: "Behind", color: "bg-red-500" };
  };
  const buildTarget = (period, line, reached) => {
    const progress = Math.round(calcAchievementPct(reached, line.bottom, line.top));
    const { status, color } = statusFromProgress(progress);
    return { period, bottom: line.bottom, top: line.top, reached, progress, status, color };
  };

  // Item vi: Target Assignments — live "reached" figures (this month / this
  // quarter / this FY) measured between each period's bottom and top line.
  const targetAssignments = [
    buildTarget("Monthly Target", targets.monthly, monthlyReached),
    buildTarget("Quarterly Target", targets.quarterly, quarterlyReached),
    buildTarget("Yearly Target", targets.yearly, yearlyReached),
  ];

  // Fallback FY-ordered (Apr->Mar) skeleton used only while metrics is still
  // loading, so the chart doesn't flash empty/misordered before the API
  // response with monthlySales arrives.
  const FY_MONTH_FALLBACK = [
    ["Apr", "Q2"], ["May", "Q2"], ["Jun", "Q2"],
    ["Jul", "Q3"], ["Aug", "Q3"], ["Sep", "Q3"],
    ["Oct", "Q4"], ["Nov", "Q4"], ["Dec", "Q4"],
    ["Jan", "Q1"], ["Feb", "Q1"], ["Mar", "Q1"],
  ].map(([month, quarter]) => ({ month, quarter, sales: 0 }));

  // Item vii: Achievements bar chart — monthly achievement % between each
  // month's bottom and top line, in FY order (starts April, matching the
  // backend's Apr->Mar financial year).
  // Each month carries its OWN lines (d.bottom / d.target = top line, from
  // sales_targets via the API). Months with no lines assigned yet use 0.
  const allAchievementsData = (metrics?.monthlySales?.length ? metrics.monthlySales : FY_MONTH_FALLBACK).map(
    (d) => {
      const monthBottom = Number(d.bottom) || 0;
      const monthTop = Number(d.target) || 0;
      return {
        ...d,
        bottom: monthBottom,
        target: monthTop,
        achievementPct: calcAchievementPct(d.sales, monthBottom, monthTop),
      };
    }
  );
  // The quarter filter narrows the monthly chart to that quarter's 3 months; "All" shows all 12 months.
  const achievementsData =
    activeQuarterTab === "All" ? allAchievementsData : allAchievementsData.filter((d) => d.quarter === activeQuarterTab);
  // Round the tallest value up to a clean axis ceiling; always at least 100 so the
  // 100%-target reference line is visible even when every month is under target.
  const rawMaxAchievement = Math.max(...allAchievementsData.map((d) => d.achievementPct), 100);
  const maxAchievement = getNiceMax(rawMaxAchievement);
  const achievementGridlines = [1, 0.75, 0.5, 0.25].map((f) => maxAchievement * f);
  // Small non-zero values still get a visible sliver instead of vanishing at ~0%
  const barHeightPct = (value) => Math.max((value / maxAchievement) * 100, value > 0 ? 3 : 0);
  // Vertical position (as % from the top) of the 100%-target reference line
  const targetLineTopPct = 100 - Math.min((100 / maxAchievement) * 100, 100);
  // Summary figure for the tab shown: the FY on "All", otherwise that
  // quarter - sales and lines are summed over the months being displayed,
  // then the same formula is applied.
  const summarySales = achievementsData.reduce((s, d) => s + (d.sales || 0), 0);
  const summaryBottom = achievementsData.reduce((s, d) => s + d.bottom, 0);
  const summaryTop = achievementsData.reduce((s, d) => s + d.target, 0);
  const fyAchievementPct = calcAchievementPct(summarySales, summaryBottom, summaryTop);
  const avgMonthlyAchievement =
    achievementsData.length > 0
      ? achievementsData.reduce((s, d) => s + d.achievementPct, 0) / achievementsData.length
      : 0;

  return (
    <div className="min-h-screen bg-[#F4F7F9] font-sans p-3 sm:p-6 mt-16 ml-[60px]">
      <div className="max-w-8xl space-y-6">

        {/* ADMIN VIEWING ANOTHER USER'S DASHBOARD */}
        {isViewingOtherUser && (
          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 text-blue-700 rounded-xl px-4 py-2.5 text-sm">
            <span className="font-semibold">
              Viewing {viewedUserName ? `${viewedUserName}'s` : "this user's"} dashboard
            </span>
            <Link
              to="/users"
              className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
            >
              <ArrowLeft size={13} />
              Back to Users
            </Link>
          </div>
        )}

        {/* DASHBOARD HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 pb-1">
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
              {dashboardTitle}
              {isViewingOtherUser ? (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">
                  {viewedUserName ? `${viewedUserName}'s data` : "User view"}
                </span>
              ) : (
                isAdmin && (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                    Org-wide (Admin)
                  </span>
                )
              )}
            </h1>
            <p className="text-[12px] text-slate-400 mt-0.5">
              {dashboardDescription} · FY {metrics?.fyLabel || "26–27"}
            </p>
            {metricsError && (
              <p className="text-[11px] text-red-500 mt-1">Couldn't load live data: {metricsError}</p>
            )}
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg p-1 text-[11px] font-semibold">
            {["All", "Q1", "Q2", "Q3", "Q4"].map((q) => (
              <button
                key={q}
                onClick={() => setActiveQuarterTab(q)}
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  activeQuarterTab === q ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-50"
                }`}
              >
                {q}
              </button>
            ))}
          </div>
        </div>

        {/* UPPER SUMMARY METRICS BLOCK (Items i to iv) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
          {topMetrics.map((card) => (
            <div
              key={card.label}
              onClick={() => {
                if (!card.path) return;
                // When viewing a specific user's dashboard (opened from the
                // Users list), carry that user through as the Account
                // Manager filter on the destination list page instead of
                // just showing everyone's records. When an admin clicks a
                // card on their OWN dashboard, activate that page's "View
                // All" toggle instead so all records show immediately
                // rather than landing on the "pick an Account Manager"
                // blank state.
                navigate(
                  card.path,
                  isViewingOtherUser
                    ? { state: { accountManager: viewedUserName } }
                    : isAdmin
                    ? { state: { viewAll: true } }
                    : undefined
                );
              }}
              className={`group relative bg-white border border-slate-200 rounded-xl p-4 transition-all duration-300 hover:shadow-lg hover:border-blue-500/30 overflow-hidden ${
                card.path ? "cursor-pointer" : "cursor-default"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-2.5 rounded-lg transition-all duration-300 ${
                    card.color === 'blue' ? 'bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white' :
                    card.color === 'amber' ? 'bg-amber-50 text-amber-600 group-hover:bg-amber-600 group-hover:text-white' :
                    'bg-green-50 text-green-600 group-hover:bg-green-600 group-hover:text-white'
                  }`}>
                    <card.icon size={18} strokeWidth={2.5} />
                  </div>

                  <div>
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      {card.label}
                    </h3>
                    <div className="flex items-baseline space-x-1">
                      <span className="text-2xl font-black text-slate-900 tracking-tight">
                        {card.value}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 pt-2 border-t border-slate-50 flex flex-col gap-0.5">
                <p className="text-[11px] font-medium text-slate-600">{card.description}</p>
                <p className="text-[10px] text-slate-400">{card.subtext}</p>
              </div>

              {/* Bottom Theme Accent Line */}
              <div className={`absolute bottom-0 left-0 h-0.5 transition-all duration-300 group-hover:w-full w-0 ${
                card.color === 'blue' ? 'bg-blue-500' :
                card.color === 'amber' ? 'bg-amber-500' : 'bg-green-500'
              }`} />
            </div>
          ))}
        </div>

        {/* MIDDLE SECTION: LINE CHART & TARGET TRACKING */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ITEM V: QUARTERLY SALES TREND LINE CHART */}
          <div className="bg-white border border-slate-200 rounded-xl lg:col-span-2 flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b bg-white">
              <div>
                <span className="text-sm font-semibold text-slate-800 block">Financial Year Sales Process</span>
                <span className="text-[11px] text-gray-400">
                  {activeQuarterTab === "All"
                    ? "Quarterly growth parameters and performance metrics trajectory"
                    : `Showing ${activeQuarterTab} performance against benchmark`}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-3 mr-2 text-[11px]">
                  <div className="flex items-center gap-1"><span className="w-2.5 h-0.5 bg-blue-600 inline-block" /> Sales</div>
                  <div className="flex items-center gap-1"><span className="w-2.5 h-0.5 bg-slate-300 border-t border-dashed inline-block" /> Benchmark</div>
                </div>
              </div>
            </div>

            {/* SVG Vector Line Chart Container */}
            <div className="p-6 flex-1 flex flex-col justify-between bg-white min-h-[280px]">
              <div className="relative w-full h-44 mt-2">

                {/* Data-driven background gridlines, scaled to the current max value */}
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                  {trendGridlines.map((g) => (
                    <div key={g} className="w-full border-t border-slate-100 text-[9px] text-slate-400 pt-0.5">
                      {formatCompactINR(g)}
                    </div>
                  ))}
                  <div className="w-full border-b border-slate-200 text-[9px] text-slate-400 pb-0.5">0</div>
                </div>

                {/* SVG Vector Layer */}
                <svg
                  className="absolute inset-0 w-full h-full"
                  viewBox={`0 0 ${trendSvgWidth} ${trendSvgHeight}`}
                  preserveAspectRatio="none"
                >
                  {/* Linear Gradient for Fill Area under the Line */}
                  <defs>
                    <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" stopOpacity="0.15" />
                      <stop offset="100%" stopColor="#2563eb" stopOpacity="0.00" />
                    </linearGradient>
                  </defs>

                  {/* Benchmark Guide Dashed Line Path */}
                  <path d={benchmarkLinePath} fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="4 4" />

                  {/* Shaded Area Layer */}
                  <path d={salesAreaPath} fill="url(#chartGradient)" />

                  {/* Primary Sales Process Line Path */}
                  <path d={salesLinePath} fill="none" stroke="#2563eb" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

                  {/* Intersect Interactive Data Nodes */}
                  {trendPoints.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.ySales} r="5" fill="#ffffff" stroke="#2563eb" strokeWidth="3" className="cursor-pointer" />
                  ))}
                </svg>

                {/* Individual Axis Floating Metric Markers */}
                <div className="absolute inset-x-0 bottom-0 flex justify-between px-[15px] translate-y-6 text-xs font-bold text-slate-600">
                  {salesTrendData.map((d) => (
                    <div key={d.quarter} className="text-center w-12">
                      {d.quarter}
                      <span className="block text-[9px] text-slate-400 font-mono font-medium">{formatCompactINR(d.sales)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 bg-slate-50 rounded-lg p-3 flex items-center justify-between text-xs text-slate-500">
                <span>
                  {activeQuarterTab === "All" ? "Peak Performance" : `${activeQuarterTab} Sales`}:{" "}
                  <strong>{peakQuarter.quarter} ({formatINR(peakQuarter.sales)})</strong>
                </span>
                <span className="text-blue-600 font-medium flex items-center gap-0.5">Average conversion: 73.8% <ArrowUpRight size={14} /></span>
              </div>
            </div>
          </div>

          {/* ITEM VI: TARGET ASSIGNED TRACKING PANEL */}
          <div className="bg-white border border-slate-200 rounded-xl flex flex-col justify-between overflow-hidden">
            <div className="p-4 border-b bg-white flex items-center gap-2">
              <Target size={18} className="text-blue-600" />
              <div>
                <span className="text-sm font-semibold text-slate-800 block">Target Assignments</span>
                <span className="text-[11px] text-gray-400">Current active financial benchmarks</span>
              </div>
            </div>

            <div className="p-4 divide-y divide-slate-100 flex-1 flex flex-col justify-between space-y-3">
              {targetAssignments.map((target) => (
                <div key={target.period} className="pt-2.5 first:pt-0 group">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-700">{target.period}</span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                      Achieved <span className="text-slate-600">{formatINR(target.reached)}</span>
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                    <span>
                      Bottom line <span className="font-semibold text-slate-500">{formatINR(target.bottom)}</span>
                    </span>
                    <span>
                      Top line <span className="font-semibold text-slate-500">{formatINR(target.top)}</span>
                    </span>
                  </div>

                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-1.5">
                    <div
                      className={`h-full ${target.color} transition-all duration-500 rounded-full`}
                      style={{ width: `${Math.min(target.progress, 100)}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between mt-1 text-[10px]">
                    <span className="text-gray-400 font-medium">{target.status}</span>
                    <span className="font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                      {target.progress}%
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-slate-50 p-3 text-center border-t border-slate-100 text-[11px] text-gray-500">
              Achievement % = (Achieved − Bottom line) ÷ (Top line − Bottom line) × 100
            </div>
          </div>

        </div>

        {/* ITEM VII: ACHIEVEMENTS — MONTHLY % ACHIEVEMENT VS TARGET BAR CHART */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 p-4 border-b bg-white">
            <div className="flex items-center gap-2">
              <BarChart3 size={18} className="text-blue-600" />
              <div>
                <span className="text-sm font-semibold text-slate-800 block">Achievements</span>
                <span className="text-[11px] text-gray-400">
                  {activeQuarterTab === "All"
                    ? `Monthly achievement between bottom & top line · FY ${metrics?.fyLabel || ""} (Apr–Mar)`
                    : `${activeQuarterTab} achievement between bottom & top line (by month) · FY ${metrics?.fyLabel || ""}`}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> Top line reached</div>
              <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-600 inline-block" /> Below top line</div>
              <div className="flex items-center gap-1"><span className="w-3 h-px border-t border-dashed border-slate-400 inline-block" /> 100% = top line (0% = bottom line)</div>
            </div>
          </div>

          <div className="p-4 sm:p-6 flex flex-col justify-between min-h-[280px]">
            <div className="relative w-full h-40 sm:h-48">
              {/* Data-driven background gridlines, scaled to the current max value */}
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                {achievementGridlines.map((g) => (
                  <div key={g} className="w-full border-t border-slate-100 text-[9px] text-slate-400 pt-0.5">
                    {g % 1 === 0 ? g : g.toFixed(1)}%
                  </div>
                ))}
                <div className="w-full border-b border-slate-200 text-[9px] text-slate-400 pb-0.5">0%</div>
              </div>

              {/* 100% target reference line */}
              <div
                className="absolute inset-x-0 border-t-2 border-dashed border-slate-400 pointer-events-none"
                style={{ top: `${targetLineTopPct}%` }}
              />

              {/* Bars */}
              <div className="absolute inset-0 flex items-end justify-between gap-1 sm:gap-3 px-1 sm:px-2">
                {achievementsData.map((d) => (
                  <div key={d.month} className="flex-1 flex flex-col items-center justify-end h-full">
                    <div className="relative group/bar flex flex-col items-center justify-end h-full w-6 sm:w-8">
                      <div className="absolute -top-1 -translate-y-full opacity-0 group-hover/bar:opacity-100 transition-opacity duration-150 bg-slate-800 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-md shadow-lg whitespace-nowrap z-10 pointer-events-none">
                        {formatCompactINR(d.sales)} · Bottom {formatCompactINR(d.bottom)} · Top {formatCompactINR(d.target)}
                      </div>
                      <span className="text-[6px] sm:text-[8px] font-bold text-slate-600 mb-1">
                        {d.achievementPct.toFixed(0)}%
                      </span>
                      <div
                        className={`w-full rounded-t-md transition-all duration-500 cursor-pointer ${
                          d.achievementPct >= 100 ? "bg-emerald-500 hover:bg-emerald-600" : "bg-blue-600 hover:bg-blue-700"
                        }`}
                        style={{ height: `${barHeightPct(d.achievementPct)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Month labels, aligned under each bar group */}
            <div className="flex items-start justify-between gap-1 sm:gap-3 px-1 sm:px-2 mt-2">
              {achievementsData.map((d) => (
                <span key={d.month} className="flex-1 text-center text-[10px] sm:text-xs font-bold text-slate-600">{d.month}</span>
              ))}
            </div>

            <div className="mt-6 bg-slate-50 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
              <span>{activeQuarterTab === "All" ? "FY Achievement" : `${activeQuarterTab} Achievement`}: <strong>{fyAchievementPct.toFixed(1)}%</strong></span>
              <span>Average Monthly Achievement: <strong>{avgMonthlyAchievement.toFixed(1)}%</strong></span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}